import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useAudioRecorder — MediaRecorder wrapper for the Whisper server-side path.
 *
 * States: supported, recording, blob, mimeType, duration (sec), error
 * Actions: start(), stop(), reset()
 *
 * Chooses the best available mimeType so the resulting file is Whisper-friendly.
 */

const CANDIDATE_MIME_TYPES = [
  { mime: "audio/webm;codecs=opus", ext: "webm" },
  { mime: "audio/webm", ext: "webm" },
  { mime: "audio/mp4;codecs=mp4a.40.2", ext: "m4a" },
  { mime: "audio/mp4", ext: "m4a" },
  { mime: "audio/mpeg", ext: "mp3" },
  { mime: "audio/wav", ext: "wav" },
];

function pickMimeType() {
  if (typeof window === "undefined" || !window.MediaRecorder) return null;
  for (const c of CANDIDATE_MIME_TYPES) {
    try {
      if (MediaRecorder.isTypeSupported(c.mime)) return c;
    } catch (_e) {
      /* older browsers may throw */
    }
  }
  return null;
}

export function isMediaRecorderSupported() {
  return typeof window !== "undefined"
    && !!window.MediaRecorder
    && !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}

export function useAudioRecorder() {
  const [supported] = useState(isMediaRecorderSupported());
  const [recording, setRecording] = useState(false);
  const [paused, setPaused] = useState(false);
  // C2 — assumed supported until a real pause()/resume() call throws (fail-soft degrade).
  const [pauseSupported, setPauseSupported] = useState(true);
  const [blob, setBlob] = useState(null);
  const [mimeMeta, setMimeMeta] = useState(null);
  const [duration, setDuration] = useState(0);
  const [error, setError] = useState("");

  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const wakeLockRef = useRef(null);
  // C2 — RECORDED time only (never wall-clock): ms accumulated across finished segments,
  // plus the currently-running segment (segmentStartRef → now) while active/not paused.
  const accumulatedMsRef = useRef(0);
  const segmentStartRef = useRef(0);

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const runTimer = () => {
    stopTimer();
    timerRef.current = setInterval(() => {
      setDuration((accumulatedMsRef.current + (Date.now() - segmentStartRef.current)) / 1000);
    }, 100);
  };

  // Keep the screen awake during recording so a phone locking itself never kills the mic.
  // C2 decision: the wake lock is held THROUGH pause (not released/reacquired) — the
  // recording session is still "open" while paused and a screen-lock mid-pause would be
  // just as disruptive as mid-recording; re-acquiring on resume also risks failing
  // silently on some mobile browsers if the tab lost visibility in between.
  const acquireWakeLock = async () => {
    try {
      if (navigator.wakeLock && typeof navigator.wakeLock.request === "function") {
        wakeLockRef.current = await navigator.wakeLock.request("screen");
      }
    } catch (_e) {
      /* fail soft — unsupported or denied */
    }
  };
  const releaseWakeLock = () => {
    try {
      if (wakeLockRef.current) {
        wakeLockRef.current.release();
        wakeLockRef.current = null;
      }
    } catch (_e) {
      /* noop */
    }
  };

  const cleanupStream = () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((t) => t.stop()); } catch (_e) { /* noop */ }
      streamRef.current = null;
    }
    recorderRef.current = null;
    releaseWakeLock();
  };

  const start = useCallback(async () => {
    if (!supported) {
      setError("Audio recording is not supported in this browser.");
      return;
    }
    setError("");
    setBlob(null);
    setDuration(0);
    setPaused(false);
    setPauseSupported(true);
    accumulatedMsRef.current = 0;
    chunksRef.current = [];

    const mime = pickMimeType();
    if (!mime) {
      setError("No supported audio format available on this browser.");
      return;
    }
    setMimeMeta(mime);

    let stream;
    try {
      // Speech-optimized capture: mono keeps 60-minute memos small (~14 MB at 32 kbps).
      stream = await navigator.mediaDevices.getUserMedia({
        audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true },
      });
    } catch (e) {
      setError("Microphone permission denied.");
      return;
    }
    streamRef.current = stream;

    let rec;
    try {
      rec = new MediaRecorder(stream, { mimeType: mime.mime, audioBitsPerSecond: 32000 });
    } catch (_e) {
      // Fallback: no explicit mimeType
      try {
        rec = new MediaRecorder(stream, { audioBitsPerSecond: 32000 });
      } catch (_e2) {
        rec = new MediaRecorder(stream);
      }
    }
    recorderRef.current = rec;

    rec.ondataavailable = (evt) => {
      if (evt.data && evt.data.size > 0) chunksRef.current.push(evt.data);
    };
    rec.onerror = (evt) => {
      setError(`Recorder error: ${evt.error?.name || "unknown"}`);
    };
    rec.onstop = () => {
      const finalBlob = new Blob(chunksRef.current, { type: mime.mime });
      chunksRef.current = [];
      setBlob(finalBlob);
      setRecording(false);
      setPaused(false);
      stopTimer();
      cleanupStream();
    };

    try {
      rec.start(250); // 250ms chunks
      accumulatedMsRef.current = 0;
      segmentStartRef.current = Date.now();
      runTimer();
      setRecording(true);
      acquireWakeLock();
    } catch (e) {
      cleanupStream();
      setError("Failed to start recording.");
    }
  }, [supported]);

  // C2 — pause/resume the SAME take (one MediaRecorder session → one blob → one transcript).
  // Fail-soft: if pause()/resume() throws (unsupported browser), the recording keeps
  // running uninterrupted and pauseSupported flips false so the UI hides the control.
  const pause = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== "recording") return;
    try {
      rec.pause();
    } catch (_e) {
      setPauseSupported(false);
      return;
    }
    stopTimer();
    accumulatedMsRef.current += Date.now() - segmentStartRef.current;
    setDuration(accumulatedMsRef.current / 1000);
    setPaused(true);
  }, []);

  const resume = useCallback(() => {
    const rec = recorderRef.current;
    if (!rec || rec.state !== "paused") return;
    try {
      rec.resume();
    } catch (_e) {
      setPauseSupported(false);
      return;
    }
    segmentStartRef.current = Date.now();
    runTimer();
    setPaused(false);
  }, []);

  const stop = useCallback(() => {
    const rec = recorderRef.current;
    if (rec && rec.state !== "inactive") {
      try { rec.stop(); } catch (_e) { /* noop */ }
    } else {
      setRecording(false);
      setPaused(false);
      stopTimer();
      cleanupStream();
    }
  }, []);

  const reset = useCallback(() => {
    stop();
    setBlob(null);
    setDuration(0);
    setPaused(false);
    setPauseSupported(true);
    accumulatedMsRef.current = 0;
    setError("");
  }, [stop]);

  useEffect(() => {
    return () => {
      stopTimer();
      cleanupStream();
    };
  }, []);

  return { supported, recording, paused, pauseSupported, blob, mimeMeta, duration, error, start, stop, pause, resume, reset };
}
